// F3 procedural cold-start warm-up (register #38).
//
// THE defect this closes (built-app, mssql): the FIRST large-buffer procedural
// completion of a session races the parse worker's ~17 s cold ANTLR bundle
// load. The worker's `getBundle()` fires lazily on the first scope/parse/syntax
// request, so a completion arriving before anything has warmed the worker
// deadlines out (empty locals for that keystroke) — and nothing re-primes.
//
// The fix: dispatch a fire-and-forget scope request at editor lifecycle
// (SqlMonaco mount / model-attach) for procedural-opener buffers, so the worker
// spawns and its bundle load STARTS while the user is still reading/positioning
// — warm before the first completion.
//
// Scope: mssql-only. The procedural-scope WORKER bridge
// (`proceduralWorkerBridge.getProceduralScope`) is used solely by the mssql
// resolver; pgsql/mysql/oracle procedural locals resolve inline in their own
// resolvers with no worker cold-start seam, so there is nothing to warm there.
//
// R8 (binding): warm-up NEVER runs on the keystroke path. It is dispatched only
// from a mount/model-attach effect (never from `onDidChangeContent` or the
// completion provider) and every failure is swallowed (fire-and-forget).
//
// Coalescing (codex P1 fix): the warm-up seeds the completion bridge's
// single-flight slot via `primeProceduralScopeWarmUp` rather than posting a raw
// `parseWorkerClient` request. A completion arriving during the cold parse then
// ATTACHES to the warm-up's in-flight job instead of stacking a duplicate full
// parse on the single-threaded worker. Warm-up still imposes NO deadline and
// mutates NONE of the completion-path stats — the keystroke path is untouched;
// the completion keeps its own 1.5 s deadline when it attaches.

import { ensureParseWorkerGeneration } from '@/features/sql-grammar-tsql/parseWorkerClient';
import { looksLikeProceduralOpener } from './mssqlProceduralLocals';
import { primeProceduralScopeWarmUp } from './proceduralWorkerBridge';

/** Once-per-(documentUri, worker-generation) latch. Maps a document URI to the
 *  worker generation it was last warmed for. A warm-up fires only when the
 *  stored generation differs from the live one, so:
 *   • a React StrictMode double-mount (two synchronous setups) warms ONCE — the
 *     second call sees the same generation already recorded and skips;
 *   • a worker respawn (new, strictly-higher generation) re-warms the fresh
 *     worker — the recorded generation no longer matches.
 *  Bounded so a long session of connection-switches (each mints a fresh
 *  documentUri) can't grow it without limit. */
const warmedGenerationByUri = new Map<string, number>();

/** Soft cap on the latch map — evict the oldest entry (Map preserves insertion
 *  order) once exceeded. Generous relative to the number of editor documents a
 *  session realistically holds open at once. */
const MAX_LATCH_ENTRIES = 256;

/**
 * Warm the parse worker for `source` if it opens a T-SQL procedural definition.
 *
 * Fire-and-forget: returns immediately, never throws, never blocks the caller.
 * A non-procedural buffer does ZERO work — the worker is not even constructed
 * (the perf guard: nothing extra happens on a plain-SELECT editor's mount).
 *
 * `documentUri` is the caller's stable per-editor document identity (the Monaco
 * model URI); it keys the warm-up latch alongside the worker generation.
 */
export function warmUpProceduralWorker(documentUri: string, source: string): void {
  // Cheap head scan FIRST — a non-procedural buffer returns here having spawned
  // nothing and touched no worker state (R8 perf guard).
  if (!looksLikeProceduralOpener(source)) return;

  try {
    // Spawn the worker (bundle load starts) and read its generation BEFORE the
    // latch check, so the latch key is stable across a StrictMode double-mount
    // (both setups see the same, now-cached, worker generation).
    const generation = ensureParseWorkerGeneration();
    if (warmedGenerationByUri.get(documentUri) === generation) return;

    warmedGenerationByUri.set(documentUri, generation);
    if (warmedGenerationByUri.size > MAX_LATCH_ENTRIES) {
      const oldest = warmedGenerationByUri.keys().next().value;
      if (oldest !== undefined) warmedGenerationByUri.delete(oldest);
    }

    // Fire-and-forget: the reply is never consumed — the request exists only to
    // pull the worker's ANTLR bundle load (and a first full-file parse) forward
    // off the completion path. Routed through the bridge's single-flight so a
    // completion during the cold parse coalesces onto THIS job (codex P1);
    // `primeProceduralScopeWarmUp` owns the rejection-swallowing.
    primeProceduralScopeWarmUp(source, source.length);
  } catch {
    // Worker construction / dispatch failure — silent. The completion path's own
    // fallback ladder (`proceduralWorkerBridge`) handles a genuinely unavailable
    // worker; warm-up must never surface an error to the editor lifecycle.
  }
}

/** Structural shape the mount-effect dispatcher needs from a Monaco text
 *  model — kept minimal so a test double can satisfy it without importing
 *  monaco types here (a real `monaco.editor.ITextModel` also satisfies it
 *  structurally). */
export interface ProceduralWarmUpModelLike {
  readonly uri: { toString(): string };
  getValue(): string;
}

/**
 * Mount-effect entry point: dispatch the warm-up from a Monaco editor's
 * model directly, rather than from pre-extracted `documentUri`/`source`
 * strings.
 *
 * F3-debt fix: `SqlMonaco`'s mount effect used to compute
 * `model.uri.toString()` itself, OUTSIDE `warmUpProceduralWorker`'s
 * try/catch — an argument-evaluation throw from a hostile/incomplete model
 * (e.g. a test double, or a model still mid-attach) escaped straight into
 * React's passive-effect flush, violating R8's "warm-up must fail silently"
 * contract even though `warmUpProceduralWorker` itself never threw. This
 * dispatcher owns ALL model access — including `uri.toString()` — inside
 * ONE try/catch, so the caller can pass the raw model straight through with
 * no unguarded access left at the call site.
 */
export function dispatchProceduralWarmUpFromModel(
  model: ProceduralWarmUpModelLike | null | undefined,
): void {
  try {
    if (!model) return;
    const documentUri = model.uri.toString();
    const source = model.getValue();
    warmUpProceduralWorker(documentUri, source);
  } catch {
    // Model access failure (missing/malformed `uri`, `getValue` throwing,
    // etc.) — silent, mirroring `warmUpProceduralWorker`'s own
    // fire-and-forget contract.
  }
}

/** Test seam — clear the warm-up latch between cases. */
export function __resetProceduralWarmUpForTest(): void {
  warmedGenerationByUri.clear();
}
